import { ReactNode } from 'react';

import get from 'lodash/get';

import { Icon } from '../icon';
import IconButton from '../icon-button/icon-button';
import { Column, ColumnConfig, RegularColumn, RowFallback } from './types';

export interface ExpanderColumn {
  accessor: ':expander';
  label: string;
}

export const EXPANDER_COLUMN_CONFIG: RegularColumn = {
  accessor: ':expander',
  width: '0',
  textAlign: 'center',
  label: 'Expand icon',
  render: (_, { isExpanded, onExpand }) => {
    return (
      <IconButton aria-label="expand row" size="small" onClick={onExpand}>
        {isExpanded ? (
          <Icon name={'KeyboardArrowUp'} />
        ) : (
          <Icon name={'KeyboardArrowDown'} />
        )}
      </IconButton>
    );
  },
};

const COLUMN_CONFIG = {
  [EXPANDER_COLUMN_CONFIG.accessor]: EXPANDER_COLUMN_CONFIG,
};

export const getColumnConfig = <Row extends RowFallback>(
  columns: Column<Row>[],
  idx: number
): ColumnConfig<Row> => {
  const column = columns[idx];
  return {
    ...(COLUMN_CONFIG[
      column.accessor as keyof typeof COLUMN_CONFIG
    ] as ColumnConfig<Row>),
    ...(column as ColumnConfig<Row>),
  };
};

export const getColumnWidth = <Row extends RowFallback>(
  column: ColumnConfig<Row>
) => {
  return column.width ?? 'auto';
};

export const getColumnAlignment = <Row extends RowFallback>(
  column: ColumnConfig<Row>
) => {
  return column.textAlign ?? 'left';
};

export const getColumnForRow = <Row extends RowFallback>(
  column: ColumnConfig<Row>,
  row: Row,
  accessor: keyof Row,
  isExpanded: boolean,
  onExpand: () => void
) => {
  if (typeof column.render === 'function') {
    return column.render(row, { isExpanded, onExpand });
  }
  const cell = get(row, accessor);
  return cell as ReactNode;
};
